/**
 * Helpers for FL-209: student ordering by team, label truncation, etc.
 */
import { Analysis, Team, User } from "../../types";

/**
 * Build a map from userId → team name based on group membership.
 * If a user belongs to multiple teams, the first team encountered wins.
 */
export function buildUserTeamMap(teams: Team[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const team of teams) {
    for (const userId of team.userIds || []) {
      if (!map.has(userId)) {
        map.set(userId, team.name);
      }
    }
  }
  return map;
}

/**
 * Sort users by team (alphabetically by team name) then by firstName within team.
 * Users without a team are placed at the end (sorted by firstName).
 */
export function sortUsersByTeamThenName(users: User[], teams: Team[]): User[] {
  const userTeamMap = buildUserTeamMap(teams);
  return [...users].sort((a, b) => {
    const teamA = userTeamMap.get(a.id);
    const teamB = userTeamMap.get(b.id);
    // Users without a team go to the end
    if (!teamA && teamB) return 1;
    if (teamA && !teamB) return -1;
    if (teamA && teamB && teamA !== teamB) {
      return teamA < teamB ? -1 : 1;
    }
    // Same team (or both teamless) → sort by firstName
    const nameA = a.firstName || "";
    const nameB = b.firstName || "";
    if (nameA === nameB) return 0;
    return nameA < nameB ? -1 : 1;
  });
}

/**
 * Sort an array of Analysis objects by their user's team (alphabetical by team
 * name) then by the user's firstName within team. Users without a team go last.
 *
 * Used to group rows by team in the Averages-style results tables.
 */
export function sortAnalysesByTeamThenName(
  analyses: Analysis[],
  teams: Team[],
): Analysis[] {
  const userTeamMap = buildUserTeamMap(teams);
  return [...analyses].sort((a, b) => {
    const teamA = userTeamMap.get(a.user.id);
    const teamB = userTeamMap.get(b.user.id);
    if (!teamA && teamB) return 1;
    if (teamA && !teamB) return -1;
    if (teamA && teamB && teamA !== teamB) {
      return teamA < teamB ? -1 : 1;
    }
    const nameA = a.user.firstName || "";
    const nameB = b.user.firstName || "";
    if (nameA === nameB) return 0;
    return nameA < nameB ? -1 : 1;
  });
}

/**
 * Wrap a long label into an array of lines suitable for use as VictoryTooltip
 * text. Each line is at most `maxCharsPerLine` chars (word-boundary wrap).
 * An optional trailing line is appended after the wrapped label (typically a
 * score summary like "(Received: 4.0)").
 *
 * Used to prevent the chart hover tooltip from overflowing the viewport with
 * long question prompts.
 */
export function wrapLabelForTooltip(
  label: string,
  trailingLine?: string,
  maxCharsPerLine = 40,
): string[] {
  const lines: string[] = [];
  const words = (label || "").split(/\s+/).filter(Boolean);
  let current = "";
  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      // Force-break a single oversized word with hyphenation.
      if (current) {
        lines.push(current);
        current = "";
      }
      let i = 0;
      while (i < word.length) {
        const chunk = word.slice(i, i + maxCharsPerLine);
        i += maxCharsPerLine;
        if (i < word.length) {
          lines.push(chunk + "-");
        } else {
          current = chunk;
        }
      }
      continue;
    }
    if (current.length === 0) {
      current = word;
    } else if (current.length + 1 + word.length <= maxCharsPerLine) {
      current += " " + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (trailingLine) lines.push(trailingLine);
  return lines;
}

/**
 * Compute a Victory tooltip flyout width that fits the longest line of a
 * (possibly multi-line) label. Estimates ~7px per char + 24px for padding;
 * Victory's auto-sizing underestimates multi-line widths, causing the first
 * line to bleed past the flyout edge.
 */
export function tooltipFlyoutWidth(label: string | string[] | undefined): number {
  const lines = Array.isArray(label) ? label : [String(label ?? "")];
  const maxLen = Math.max(0, ...lines.map((l) => l.length));
  return Math.max(60, maxLen * 7 + 24);
}

/**
 * Truncate text at a word boundary near the given character limit.
 * If the text is already short enough, returns it unchanged.
 * Otherwise, returns the longest word-boundary prefix that fits within `maxChars`,
 * suffixed with "…".
 *
 * Example: truncateAtWordBoundary("How effectively did the team collaborate?", 20)
 *   → "How effectively did…"
 */
export function truncateAtWordBoundary(text: string, maxChars: number): string {
  if (!text || text.length <= maxChars) return text;
  const ellipsis = "…";
  const budget = maxChars - ellipsis.length;
  if (budget <= 0) return ellipsis;

  // Walk word by word, accumulating the longest run that fits in `budget`.
  // Splitting with a capturing group keeps the whitespace separators so the
  // running length stays accurate.
  const tokens = text.split(/(\s+)/);
  let result = "";
  for (const token of tokens) {
    if ((result + token).length > budget) break;
    result += token;
  }
  result = result.trimEnd();
  if (!result) {
    // First word longer than budget — fall back to hard truncation.
    return text.slice(0, budget) + ellipsis;
  }
  return result + ellipsis;
}
